import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, CircularProgress } from '@mui/material';
import { AddIcon } from 'icons/materialIcons';
import { useCardContext } from 'contexts/CardContext';
import { useParams } from 'react-router-dom';
import { updateLeadCaptureMode, getCardById } from 'apis/cardApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import { useAuth } from 'contexts/AuthContext';
import TeamIosDarkToggle from 'team/shared/components/button/TeamIosDarkToggle';

const TeamLinksToggle = ({ onAddLinksAndContact }) => {
    // Get context and params
    const { cardState, setLeadCaptureModeOn } = useCardContext();
    const { cardId: paramCardId } = useParams();
    const { authState } = useAuth();
    const userId = authState.user?.userId;

    // ✅ TEMPLATE CONTEXT: Use authenticated user's card if no cardId from params
    // Check if we're in template edit page by checking the URL path
    const isTemplateContext = !paramCardId || window.location.pathname.includes('/template/');
    const userCardId = authState.user?.activeCardId ||
        (authState.user?.contactCardIds?.length > 0 ? authState.user.contactCardIds[0] : null);
    const cardId = isTemplateContext ? userCardId : paramCardId;

    // EXPLICIT STATE DECLARATIONS
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Get lead capture mode state with safe fallback
    const leadCaptureModeOn = cardId ? (cardState[cardId]?.leadCaptureModeOn ?? false) : false;

    // Fetch card data and initialize settings
    useEffect(() => {
        const fetchCardData = async () => {
            if (!cardId || isInitialized) return;

            try {
                setIsLoading(true);

                // ✅ FIX: Check if this is a new card creation (UUID)
                const isNewCardCreation = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

                if (isNewCardCreation) {
                    // ✅ NEW CARD: Use default value (false) for lead capture mode
                    console.log('🔍 TeamLinksToggle: New card creation - using default value (false)');
                    setLeadCaptureModeOn(cardId, false);
                } else {
                    // ✅ EXISTING CARD OR TEMPLATE: Get card-specific setting from server
                    const response = await getCardById(cardId);

                    if (response && response.card) {
                        const cardSetting = response.card.leadCaptureModeOn;
                        console.log('🔍 TeamLinksToggle: Server value:', cardSetting);

                        // Update CardContext with server value
                        setLeadCaptureModeOn(cardId, Boolean(cardSetting));
                    }
                }

                setIsInitialized(true);
            } catch (error) {
                console.error('Failed to load lead capture settings:', error);
                // ✅ FALLBACK: Use default value on error
                if (cardId) {
                setLeadCaptureModeOn(cardId, false);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchCardData();
    }, [cardId, userId, isInitialized, setLeadCaptureModeOn]);

    // Handle toggle change
    const handleToggle = (event) => {
        if (isLoading) return;

        const isChecked = event.target.checked;
        console.log("Toggle switched:", isChecked);

        // Update local state first for immediate feedback
        setLeadCaptureModeOn(cardId, isChecked);

        // Update on the server using dedicated API
        updateLeadCaptureMode(cardId, isChecked)
            .then(() => {
                console.log("API call successful");

                setSnackbar({
                    open: true,
                    message: isChecked ? 'Lead capture mode enabled' : 'Lead capture mode disabled',
                    severity: 'success'
                });
            })
            .catch(error => {
                console.error("API call failed:", error);
                // Revert on error
                setLeadCaptureModeOn(cardId, !isChecked);

                setSnackbar({
                    open: true,
                    message: 'Failed to update lead capture mode',
                    severity: 'error'
                });
            });
    };

    // ✅ CHECK: Hide lead capture toggle during new card creation or if no cardId
    const isNewCardCreation = cardId && cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;
    const shouldShowToggle = !isNewCardCreation && cardId;

    return (
        <div>
            <Box display="flex" alignItems="center" gap={1}>
                {/* ✅ HIDE: Lead capture toggle during new card creation or template context without user card */}
                {shouldShowToggle && (
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '20px',
                            padding: '8px 16px' // Increased padding to match individual plan visual appearance
                        }}
                    >
                        <Box display="flex" alignItems="center">
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: "#4f4f4f",
                                    marginLeft: 0,
                                    marginRight: '8px' // Increased margin to match spacing
                                }}
                            >
                                Lead Capture
                            </Typography>
                            {isLoading ? (
                                <CircularProgress size={20} />
                            ) : (
                                <TeamIosDarkToggle
                                    checked={Boolean(leadCaptureModeOn)}
                                    onChange={handleToggle}
                                />
                            )}
                        </Box>
                    </Box>
                )}

                <Button
                    variant="contained"
                    onClick={() => onAddLinksAndContact()}
                    sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#333',
                        },
                        display: 'flex',
                        alignItems: 'center',
                    }}
                    startIcon={<AddIcon sx={{ transform: 'scaleX(-1)' }} />}
                >
                    Add Links and contact Info
                </Button>
            </Box>

            {/* Snackbar */}
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
}

export default TeamLinksToggle;